import {match} from 'ts-pattern';

import {AccountingOperationResponseBody, AccountingProcessResponseBody} from '@dms/api/sales';

/**
 * The states an external accounting process can be shown in, in the order they
 * normally occur. Kept here rather than imported from the business case module so
 * developer settings does not depend on it — this is the inverse mapping of what
 * the payment screen derives, and it exists only to drive the simulation.
 */
export const DEBUG_ACCOUNTING_MILESTONES = [
  'waiting',
  'accepted',
  'draft',
  'issued',
  'documentAvailable',
  'failed',
  'canceled',
] as const;

export type DebugAccountingMilestone = (typeof DEBUG_ACCOUNTING_MILESTONES)[number];

const buildAccountingProcess = (
  milestone: DebugAccountingMilestone
): AccountingProcessResponseBody =>
  match<DebugAccountingMilestone, AccountingProcessResponseBody>(milestone)
    .with('waiting', () => ({
      request: {accepted: false, executionId: null, targetUrl: null},
      document: {state: 'none', documentId: null},
      printout: {state: 'none', fileId: null},
      binding: {state: 'none', taxDocumentId: null},
      intent: 'balance',
      mode: 'external',
      summaryState: 'requested',
    }))
    .with('accepted', () => ({
      request: {accepted: true, executionId: 'debug-execution-id', targetUrl: null},
      document: {state: 'none', documentId: null},
      printout: {state: 'none', fileId: null},
      binding: {state: 'none', taxDocumentId: null},
      intent: 'balance',
      mode: 'external',
      summaryState: 'in_progress',
    }))
    .with('draft', () => ({
      request: {accepted: true, executionId: 'debug-execution-id', targetUrl: null},
      document: {state: 'draft', documentId: 'debug-document-id'},
      printout: {state: 'none', fileId: null},
      binding: {state: 'none', taxDocumentId: null},
      intent: 'balance',
      mode: 'external',
      summaryState: 'in_progress',
    }))
    .with('issued', () => ({
      request: {accepted: true, executionId: 'debug-execution-id', targetUrl: null},
      document: {state: 'finalized', documentId: 'debug-document-id'},
      printout: {state: 'none', fileId: null},
      binding: {state: 'none', taxDocumentId: null},
      intent: 'balance',
      mode: 'external',
      summaryState: 'in_progress',
    }))
    .with('documentAvailable', () => ({
      request: {accepted: true, executionId: 'debug-execution-id', targetUrl: null},
      document: {state: 'finalized', documentId: 'debug-document-id'},
      printout: {state: 'available', fileId: 'debug-file-id'},
      binding: {state: 'none', taxDocumentId: null},
      intent: 'balance',
      mode: 'external',
      summaryState: 'completed',
    }))
    .with('failed', () => ({
      request: {accepted: true, executionId: 'debug-execution-id', targetUrl: null},
      document: {state: 'none', documentId: null},
      printout: {state: 'none', fileId: null},
      binding: {state: 'none', taxDocumentId: null},
      intent: 'balance',
      mode: 'external',
      summaryState: 'failed',
    }))
    .with('canceled', () => ({
      request: {accepted: true, executionId: 'debug-execution-id', targetUrl: null},
      document: {state: 'draft', documentId: 'debug-document-id'},
      printout: {state: 'none', fileId: null},
      binding: {state: 'none', taxDocumentId: null},
      intent: 'balance',
      mode: 'external',
      summaryState: 'canceled',
    }))
    .exhaustive();

/**
 * Builds a whole accounting operation for a simulated milestone.
 *
 * The embedded payment is a placeholder: the state UI reads only
 * `accountingProcess`, and filling it from the real order would tie the
 * simulation to whichever payment happens to be cached.
 */
export const buildDebugAccountingOperation = (
  paymentId: string,
  milestone: DebugAccountingMilestone
): AccountingOperationResponseBody => ({
  orderPayment: {
    id: paymentId,
    paymentDiscriminator: 'BALANCE',
    paymentMethod: 'BANK_TRANSFER',
    amount: null,
    payDate: null,
    issuedOn: null,
    dueDate: null,
    customerBankAccountId: null,
    tenantBankAccount: null,
    cashRegisterId: null,
    paymentState: 'CONCEPT',
    files: [],
    comment: null,
    invoices: [],
    proformaInvoices: [],
    taxableDocuments: [],
    cashReceipts: [],
    correctiveTaxDocuments: [],
    invoiceId: null,
    proformaInvoiceId: null,
    taxDocumentId: null,
    internalInvoiceDocuments: [],
    internalCorrectiveTaxDocuments: [],
    createCashRegisterDocument: null,
    cashRegisterDocumentId: null,
    exchangeRate: null,
    foreignCurrencyAmount: null,
    supplierInvoiceNumber: null,
    dateOfTaxableSupply: null,
  },
  accountingProcess: buildAccountingProcess(milestone),
});
